import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';

// Load auth token from localStorage on initialization
const loadAuthToken = () => {
  try {
    const tokenData = localStorage.getItem('cl.authToken');
    if (!tokenData) return null;

    const parsed = JSON.parse(tokenData);

    // Check if token has expiry timestamp and if it's expired
    if (parsed.expiresAt && Date.now() >= parsed.expiresAt) {
      // eslint-disable-next-line no-console
      console.log('Auth token expired, clearing storage');
      localStorage.removeItem('cl.authToken');
      localStorage.removeItem('cl.userInfo');
      return null;
    }

    return parsed;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading auth token:', error);
    return null;
  }
};

// Load user info from localStorage on initialization
const loadUserInfo = () => {
  try {
    // Check if auth token is valid first
    const tokenData = localStorage.getItem('cl.authToken');
    if (!tokenData) return null;

    const parsedToken = JSON.parse(tokenData);
    if (parsedToken.expiresAt && Date.now() >= parsedToken.expiresAt) {
      // Token expired, don't load user info
      localStorage.removeItem('cl.userInfo');
      return null;
    }

    const userInfo = localStorage.getItem('cl.userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading user info:', error);
    return null;
  }
};

// Helper to check if token is completely expired
const isTokenExpired = (token) => {
  if (!token || !token.expiresAt) return true;
  return Date.now() >= token.expiresAt;
};

const initialState = {
  status: 'idle',
  authToken: loadAuthToken(),
  userInfo: loadUserInfo(),
  error: null,
};

export const fetchUserInfo = createAsyncThunk(
  'users/fetchUserInfo',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userToken = state.users.authToken;

    if (!userToken || isTokenExpired(userToken)) {
      localStorage.removeItem('cl.authToken');
      localStorage.removeItem('cl.userInfo');
      return thunkAPI.rejectWithValue('Token expired or invalid');
    }

    const { usersApi } = thunkAPI.extra;
    return usersApi.getMe();
  },
);

export const updateUserInfo = createAsyncThunk(
  'users/updateUserInfo',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const userToken = state.users.authToken;

    if (!userToken || isTokenExpired(userToken)) {
      localStorage.removeItem('cl.authToken');
      localStorage.removeItem('cl.userInfo');
      return thunkAPI.rejectWithValue('Token expired or invalid');
    }

    const { usersApi } = thunkAPI.extra;
    return usersApi.updateMe(payload);
  },
);

export const loginWithGoogle = createAsyncThunk(
  'users/loginWithGoogle',
  async ({ code, redirectUri }, thunkAPI) => {
    const { usersApi } = thunkAPI.extra;
    return usersApi.loginWithGoogle(code, redirectUri);
  },
);

export const refreshAuthToken = createAsyncThunk(
  'users/refreshAuthToken',
  async (_, thunkAPI) => {
    const { usersApi } = thunkAPI.extra;
    try {
      const json = await usersApi.refreshToken();
      return json.tokens;
    } catch (error) {
      // If refresh fails, clear any stale auth data
      localStorage.removeItem('cl.authToken');
      localStorage.removeItem('cl.userInfo');
      return thunkAPI.rejectWithValue(error.message || 'Refresh failed');
    }
  },
);

export const logoutUser = createAsyncThunk(
  'users/logoutUser',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState();
    const userToken = state.users.authToken;

    if (userToken) {
      // Call backend to clear refresh token
      const { usersApi } = thunkAPI.extra;
      await usersApi.logout();
    }

    // Clear local storage
    localStorage.removeItem('cl.authToken');
    localStorage.removeItem('cl.userInfo');

    return null;
  },
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setDarkMode(state, action) {
      if (state.userInfo) {
        state.userInfo.darkMode = action.payload;
        localStorage.setItem('cl.userInfo', JSON.stringify(state.userInfo));
      }
    },
    setAnyLanguage(state, action) {
      if (state.userInfo) {
        state.userInfo.anyLanguage = action.payload;
        localStorage.setItem('cl.userInfo', JSON.stringify(state.userInfo));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.userInfo = action.payload;
        // Persist to localStorage
        if (action.payload) {
          localStorage.setItem('cl.userInfo', JSON.stringify(action.payload));
        }
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        // If token expired, clear auth state
        if (action.payload === 'Token expired or invalid') {
          state.authToken = null;
          state.userInfo = null;
        }
      })
      .addCase(updateUserInfo.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserInfo.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Merge the update with existing userInfo to preserve all fields
        if (action.payload && state.userInfo) {
          state.userInfo = { ...state.userInfo, ...action.payload };
          localStorage.setItem('cl.userInfo', JSON.stringify(state.userInfo));
        } else if (action.payload) {
          state.userInfo = action.payload;
          localStorage.setItem('cl.userInfo', JSON.stringify(action.payload));
        }
      })
      .addCase(updateUserInfo.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        // If token expired, clear auth state
        if (action.payload === 'Token expired or invalid') {
          state.authToken = null;
          state.userInfo = null;
        }
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const { tokens } = action.payload;

        // Add expiry timestamp (expires_in is in seconds, convert to ms)
        const tokenWithExpiry = {
          ...tokens,
          expiresAt: tokens.expires_in
            ? Date.now() + (tokens.expires_in * 1000)
            : Date.now() + (3600 * 1000), // Default 1 hour if not provided
        };

        state.authToken = tokenWithExpiry;
        // userInfo will be set by fetchUserInfo() - don't set it here
        // Persist only token to localStorage
        localStorage.setItem('cl.authToken', JSON.stringify(tokenWithExpiry));
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(refreshAuthToken.pending, () => {
        // Don't set status to loading during silent refresh
      })
      .addCase(refreshAuthToken.fulfilled, (state, action) => {
        const tokens = action.payload;

        // Add expiry timestamp
        const tokenWithExpiry = {
          ...tokens,
          expiresAt: tokens.expires_in
            ? Date.now() + (tokens.expires_in * 1000)
            : Date.now() + (3600 * 1000),
        };

        state.authToken = tokenWithExpiry;
        localStorage.setItem('cl.authToken', JSON.stringify(tokenWithExpiry));
        state.status = 'succeeded';
      })
      .addCase(refreshAuthToken.rejected, (state) => {
        // Refresh failed, log user out
        localStorage.removeItem('cl.authToken');
        localStorage.removeItem('cl.userInfo');
        state.authToken = null;
        state.userInfo = null;
        state.status = 'idle';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.authToken = null;
        state.userInfo = null;
        state.status = 'idle';
      });
  },
});

export default usersSlice.reducer;

export const {
  setDarkMode, setAnyLanguage,
} = usersSlice.actions;
